import {
    AfterViewInit,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';

import {
    GaugeChartData,
    BarChartData,
    electricMobilityData,
    PvSurplusResult,
    PvTotalityResult,
    WithRoi,
} from 'src/app/models/Solar';

import { CadastreSolaireContactModalComponent } from '../../../../../../modals/contact/cadastre-solaire-contact-modal.component';
import { CadastreSolaireResultCardComponent } from '../../../../../result-card/cadastre-solaire-result-card.component';

import { LocalStorageService } from 'src/app/services/local-storage.service';
import { SolarPvSurplusService } from 'src/app/services/cadastre-solaire/cadastre-solaire-pv-surplus.service';
import { SolarPvTotalityService } from 'src/app/services/cadastre-solaire/cadastre-solaire-pv-totality.service';
import { SolarService } from 'src/app/services/cadastre-solaire/cadastre-solaire.service';
import { UsefulService } from 'src/app/services/UsefulService';

import { environment } from 'src/environments/environment';

import { CadastreSolaireInfoModalComponent } from '../../../../../../modals/info/cadastre-solaire-info-modal.component';
import { distinctUntilChanged, skip, Subject, takeUntil } from 'rxjs';

@UntilDestroy()
@Component({
    selector: 'cadastreSolairePvSurplusSimulation',
    templateUrl: './cadastre-solaire-pv-surplus-simulation.template.html',
    styleUrls: ['./cadastre-solaire-pv-surplus-simulation.component.scss'],
    standalone: false,
})
export class CadastreSolairePvSurplusSimulationComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    private destroy$ = new Subject<void>();

    @ViewChild('resultInvestment') resultInvestment!: CadastreSolaireResultCardComponent;
    @ViewChild('bankLoanCheckboxTemplate', { static: true })
    bankLoanCheckboxTemplate!: TemplateRef<any>;

    public isSimulateBankLoanChecked = false;

    public showMobileUi: boolean = false;
    public showLoader: boolean = true;

    public modularity: any;

    public slider: {
        maxModuleCount: number;
        moduleCount: number;
        moduleArea: number;
        peakPower: number;
        stringifiedPeakPower: string;
    };

    public optimalSelfConsumption: {
        moduleCount: number;
        stringifiedModuleCount: string;
        rate: number;
        stringifiedRate: string;
        peakPower: number;
        stringifiedPeakPower: string;
    };

    public tooltipPvInvestmentDetail: string;
    public tooltipPvProductionDetail: string;
    public tooltipPvCarbonImpactsDetail: string;
    public tooltipPvSellingDetail: string;
    public tooltipPvElectricMobility: string;

    public adjustInstallationSizeLabel: string;
    public contactButtonLabel: string;

    public htmlPvInstallation: any;
    public htmlPvInvestment: string;
    public htmlPvProduction: string;
    public htmlPvCarbonImpacts: string;
    public htmlPvProductionDetail: string;
    public htmlPvConsumptionDetail: string;
    public htmlPvElectricMobilityDetail: string;

    public htmlPvEconomy: string;
    public htmlPvElectricMobility: string;
    public htmlPvWarning: string;
    public htmlPvWarningSubcribedPower: string;
    public htmlPvWarningOverHundred: string;
    public htmlPvWarningOverFiveHundred: string;

    public warningMessageEconomy: string;

    public activeCollapseWarningPower: boolean = false;
    public isOverHundredWarningActive: boolean = false;
    public isOverFiveHundredWarningActive: boolean = false;
    public isOverSubscribedWarningActive: boolean = false;

    public isResultsDownloading: boolean;
    private gaugeChartData: GaugeChartData;
    private barChartData: BarChartData;
    private electricMobilityData: electricMobilityData;

    public connectionCost: {
        isAvailable: boolean;
        isActive: boolean;
        isLoading: boolean;
        value: number | null;
        message: string;
    };

    public titleInvestment: string = 'Investissement';
    public titleProduction: string = 'Production';
    public titleCarbonImpacts: string = 'Impacts';
    public titleEconomy: string = 'Economies';
    public titleElectricMobility: string = 'Mobilité électrique';

    public isElectricMobilityDefined: boolean = false;

    public currentResult: PvSurplusResult;

    constructor(
        private modalService: NgbModal,
        private notification: ToastrService,
        @Inject(LocalStorageService) private localStorageService: LocalStorageService,
        @Inject(SolarPvSurplusService)
        public solarPvSurplusService: SolarPvSurplusService,
        @Inject(SolarPvTotalityService) private solarPvTotalityService: SolarPvTotalityService,
        @Inject(SolarService) public solarService: SolarService,
        @Inject(UsefulService) private usefulService: UsefulService,
    ) {}

    async ngOnInit(): Promise<void> {
        this.showMobileUi = this.usefulService.showMobileUi;
        this.modularity = this.localStorageService.get('preferences').modularity.solar;

        // this.tooltipPvInfoDetail = this.solarPvTotalityService.pvInfo.message.info_detail;
        this.tooltipPvInvestmentDetail =
            this.solarPvTotalityService.pvInfo.message.investissement_detail;
        this.tooltipPvProductionDetail =
            this.solarPvTotalityService.pvInfo.message.production_detail;
        this.tooltipPvCarbonImpactsDetail =
            this.solarPvTotalityService.pvInfo.message.impact_carbone_detail;
        this.tooltipPvSellingDetail = this.solarPvTotalityService.pvInfo.message.economie_detail;
        this.tooltipPvElectricMobility =
            this.solarPvTotalityService.pvInfo.message.mobilite_electrique_detail;
        this.htmlPvWarning = this.solarPvTotalityService.pvInfo.message.warning;
        this.htmlPvWarningSubcribedPower =
            this.solarPvTotalityService.pvInfo.message.warning_subcribed_power;
        this.htmlPvWarningOverHundred = this.solarPvSurplusService.pvInfo.message.surplus.over_100;
        this.htmlPvWarningOverFiveHundred =
            this.solarPvSurplusService.pvInfo.message.surplus.over_500;

        const customAdjustInstallationSizeLabel =
            this.solarService.moduleInfo.ajuster_taille_installation;
        this.adjustInstallationSizeLabel = customAdjustInstallationSizeLabel
            ? customAdjustInstallationSizeLabel
            : "J'ajuste la taille de mon installation";

        const customContactButtonLabel = this.solarService.moduleInfo.contacts_bouton;
        this.contactButtonLabel = customContactButtonLabel
            ? customContactButtonLabel
            : 'Contacter un expert';

        if (this.solarPvSurplusService.equipmentsData.usableFloorArea == null) {
            this.solarPvSurplusService.equipmentsData.usableFloorArea =
                this.solarPvSurplusService.setDefaultUsableFloorArea();
            this.solarPvSurplusService.equipmentsData.occupantCount =
                this.solarPvSurplusService.setDefaultOccupantCount();
        }

        this.isElectricMobilityDefined =
            !this.solarPvSurplusService.checkIsImportedElectricLoadDefined() &&
            this.solarPvSurplusService.checkIsElectricMobilityDefined();

        this.solarService.bankLoanResultObs$
            .pipe(skip(1), takeUntil(this.destroy$), distinctUntilChanged())
            .subscribe((bankLoanResult) => {
                if (bankLoanResult) {
                    const newRoi = bankLoanResult.roi;
                    const newInvestment = bankLoanResult.investment;
                    const interest = bankLoanResult.interest;
                    const originalRoi = this.solarPvSurplusService.originalRoi;

                    this.solarPvSurplusService.currentResult.roi = newRoi;
                    this.solarPvSurplusService.currentResult.investment = newInvestment;
                    this.solarPvSurplusService.roi = newRoi;
                    this.solarPvSurplusService.investment = newInvestment;

                    this._updatePvInvestment(interest);
                    this._updateAutoconsumptionGain(originalRoi);

                    if (!this.modularity.simulations) {
                        this.solarService.closeSimulateBankLoan();
                    } else {
                        this.validate();
                        this.solarService.closeSimulateBankLoan();
                    }
                }
            });
    }

    async ngAfterViewInit(): Promise<void> {
        this.resultInvestment.connectionCostClick$
            .pipe(untilDestroyed(this))
            .subscribe(async () => {
                await this.getConnectionCost();
            });

        await this.initPvSurplusSimulation();

        this.initConnectionCost();

        const maxResult = this.solarPvSurplusService.getMaxModuleResult();
        const optimalResult = this.solarPvSurplusService.findOptimalResult();
        this.solarPvSurplusService.setCurrentResult(optimalResult);
        this.currentResult = optimalResult;

        const pvTotalityResult = this.solarPvTotalityService.findResultByModuleCount(
            optimalResult.moduleCount,
        );
        if (!pvTotalityResult) return;
        this.solarPvTotalityService.setCurrentResult(pvTotalityResult);

        this.slider = {
            maxModuleCount: maxResult.moduleCount,
            moduleCount: optimalResult.moduleCount,
            moduleArea: this.usefulService.round(optimalResult.totalModuleArea),
            peakPower: optimalResult.peakPower,
            stringifiedPeakPower: this.usefulService.stringifyNumber(
                this.usefulService.ceil(optimalResult.peakPower, 1),
            ),
        };

        this._updatePvIndicator();

        this.optimalSelfConsumption = {
            moduleCount: optimalResult.moduleCount,
            stringifiedModuleCount: this.usefulService.stringifyNumber(optimalResult.moduleCount),
            rate: optimalResult.selfConsumptionRate,
            stringifiedRate: this.usefulService.stringifyNumber(
                this.usefulService.round(optimalResult.selfConsumptionRate * 100),
            ),
            peakPower: optimalResult.peakPower,
            stringifiedPeakPower: this.usefulService.stringifyNumber(
                this.usefulService.ceil(optimalResult.peakPower, 1),
            ),
        };

        const isNew = this.solarService.checkIfSelectedElementsIsNew();
        if (isNew) {
            this.solarService.setTotalModuleAreaToSelectionInfo(maxResult.totalModuleArea);
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initConnectionCost() {
        this.connectionCost = {
            // isAvailable: this.solarPvSurplusService.consumptionData.subscribedPower <= 36, // api enedis does not work beyond 36kVA
            isAvailable: true,
            isLoading: false,
            isActive: true,
            value: null,
            message: '',
        };
    }

    private async initPvSurplusSimulation() {
        try {
            this.showLoader = true;
            const response = await this.solarPvSurplusService.simulatePvSurplus();
            this.solarPvTotalityService.setResults(response.totality);
        } catch (error) {
            console.error('Error initPvSurplusSimulation', error);
            this.notification.error(
                "Une erreur est survenue. Impossible d'initialiser la simulation photovoltaïque en autoconsommation.",
            );
            this.solarPvSurplusService.updateStage('consumptionStage');
        } finally {
            this.showLoader = false;
        }
    }

    cancel() {
        this.solarPvSurplusService.updateStage('consumptionStage');
    }

    private _updatePvIndicator() {
        if (this.solarPvSurplusService.currentResult.connectionCost) {
            this.connectionCost = this.solarPvSurplusService.currentResult.connectionCost;
        }

        this._updatePvInvestment();
        this._updatePvInstallation();
        this._updatePvProduction();
        this._updatePvCarbonImpacts();

        if (this.isElectricMobilityDefined) {
            this._updatePvElectricMobility();
            this._setPvElectricMobilityDetail();
        }

        this._updateAutoconsumptionGain();
        this._updateAutoConsumptionDetail();

        this.isOverHundredWarningActive = this._displayOverHundredWarning();
        this.isOverFiveHundredWarningActive = this._displayOverFiveHundredWarning();
        this.isOverSubscribedWarningActive = this._displayWarningSubcribedPower();
    }

    private _updatePvInvestment(interest?: number) {
        const splitByInvestmentTag =
            this.solarPvTotalityService.pvInfo.message.investissement.split('##pv_investment');
        if (splitByInvestmentTag.length === 2) {
            let tag = `<span class="text-st-active">${this._stringifyInvestment()}</span>`;
            if (interest) {
                tag += `, dont <span class="text-st-active">${this._stringifyInterest(interest)}</span> d'intérêts d'emprunt bancaire`;
            }
            let html = splitByInvestmentTag.join(tag);

            if (!!this.solarPvSurplusService.bonus) {
                html = html[html.length - 1] == '.' ? html.slice(0, -1) : html;
                html += `, après déduction des <span class="text-st-active">${this._stringifyBonus()}</span> de prime à l'autoconsommation.`;
            }

            if (this.solarService.connectionCostInfo.isEnedisAvailable) {
                if (!this.connectionCost.isAvailable) {
                    this.htmlPvInvestment = `
                        <div>
                            ${html}
                            <div>
                                <small>
                                    Le coût du raccordement ne peut être estimé par Enedis, car la puissance souscrite est supérieure à 36\u00a0kVA.
                                </small>
                            </div>
                        </div>
                    `;
                    return;
                }

                if (this.connectionCost.value || this.connectionCost.value === 0) {
                    const connectionCostStringified = this.usefulService.stringifyNumber(
                        this.connectionCost.value,
                    );
                    const initHtml = html.slice(0, -1);
                    this.htmlPvInvestment = `
                        <div class="text-start">
                           ${initHtml}, dont ${connectionCostStringified}\u00a0€ de raccordement.
                            <div>
                                <small>
                                    Coût de raccordement estimé grâce à Enedis.
                                </small>
                            </div>
                        </div>
                    `;
                    return;
                }

                if (this.connectionCost.message) {
                    this.htmlPvInvestment = `
                        <div>
                            ${html}
                            <div>Le coût du raccordement n'a pas pu être estimé. ${this.connectionCost.message}</div>
                        </div>
                    `;
                    return;
                }

                this.htmlPvInvestment = `
                    <div>
                        ${html}
                    </div>
                `;
            } else {
                let message: string;
                const connectionCostInfo = this.solarService.connectionCostInfo;
                if (connectionCostInfo.contactRegie) {
                    message = connectionCostInfo.contactRegie;
                } else if (connectionCostInfo.aecEstimation) {
                    message = connectionCostInfo.aecEstimation;
                } else {
                    message = '';
                }
                const htmlPvInvestment = `
                    <div>
                        ${html}
                        <div class="d-pdf-none">${message}</div>
                    </div>
                `;

                this.htmlPvInvestment = htmlPvInvestment;
            }
        }
    }

    private _stringifyInvestment() {
        const investment = this.usefulService.round(this.solarPvSurplusService.investment);
        const investmentStringified = this.usefulService.stringifyNumber(investment);
        return `${investmentStringified}\u00a0€`;
    }

    private _stringifyInterest(interest: number) {
        const interestStringified = this.usefulService.stringifyNumber(Math.round(interest));
        return `${interestStringified}\u00a0€`;
    }

    private _stringifyBonus() {
        const bonus = Math.round(this.solarPvSurplusService.bonus);
        const bonusStringified = this.usefulService.stringifyNumber(bonus);
        return `${bonusStringified}\u00a0€`;
    }

    private _updatePvInstallation() {
        let html = this.solarPvTotalityService.pvInfo.message.installation;

        // ##pv_module_count
        const isPvModuleCountDefined = html.includes('##pv_module_count');
        if (isPvModuleCountDefined) {
            let unit = this.solarPvSurplusService.totalModuleCount > 1 ? 'modules' : 'module';
            const pvModuleCountStringified = this.usefulService.stringifyNumber(
                this.solarPvSurplusService.totalModuleCount,
            );
            const placeholder = `<span class="text-st-active">${pvModuleCountStringified}&nbsp;${unit}</span>`;
            html = html.replace('##pv_module_count', placeholder);
        }

        // ##pv_total_module_area
        const isPvTotalModuleAreaDefind = html.includes('##pv_total_module_area');
        if (isPvTotalModuleAreaDefind) {
            const roundedValue = this.usefulService.round(
                this.solarPvSurplusService.totalModuleArea,
            );
            const pvTotalModuleAreaStringified = this.usefulService.stringifyNumber(roundedValue);
            const placeholder = `<span class="text-st-active">${pvTotalModuleAreaStringified}&nbsp;m²</span>`;
            html = html.replace('##pv_total_module_area', placeholder);
        }

        // ##pv_power_peak
        const isPvPowerPeakDefined = html.includes('##pv_power_peak');
        if (isPvPowerPeakDefined) {
            const placeholder = `<span class="text-st-active">${this._stringifyPeakPower()}</span>`;
            html = html.replace('##pv_power_peak', placeholder);
        }

        html = `<span>${html}</span>`;
        this.htmlPvInstallation = html;
    }

    private _stringifyPeakPower() {
        const peakPower = this.usefulService.round(this.solarPvSurplusService.totalPowerPeak, 1);
        const powerPeakStringified = this.usefulService.stringifyNumber(peakPower);
        return `${powerPeakStringified}\u00a0kWc`;
    }

    private _updatePvProduction() {
        // ##pv_production_by_year
        const splitByProductionTag =
            this.solarPvTotalityService.pvInfo.message.production.split('##pv_production_by_year');
        if (splitByProductionTag.length === 2) {
            let html = splitByProductionTag.join(
                `<span class="text-st-active">${this._stringifyProduction()}</span>`,
            );

            // ##housing_consumption_by_year_ratio
            const splitByHousingTag = html.split('##housing_consumption_by_year_ratio');
            if (splitByHousingTag.length === 2) {
                // dirty fix : actually we don't want to display the second part of the message
                html = splitByHousingTag[0].split(',')[0] + '.';
            }

            const selfConsumptionRate = Math.round(
                this.solarPvSurplusService.selfConsumptionRate * 100,
            );
            const selfConsumptionRateStringified =
                this.usefulService.stringifyNumber(selfConsumptionRate);
            html += `<div>Le taux d'autoconsommation de l'électricité produite est de <span class="text-st-active">${selfConsumptionRateStringified}\u00a0%</span>.</div>`;

            html = `<div>${html}</div>`;
            this.htmlPvProduction = html;
        }
    }

    private _stringifyProduction() {
        const pvProduction = Math.round(this.solarPvSurplusService.totalProduction);
        const pvProductionStringified = this.usefulService.stringifyNumber(pvProduction);
        return `${pvProductionStringified}\u00a0kWh/an`;
    }

    private _updatePvCarbonImpacts() {
        // ##pv_carbon_emissions_saved
        const splitByTag = this.solarPvTotalityService.pvInfo.message.impact_carbone.split(
            '##pv_carbon_emissions_saved',
        );
        if (splitByTag.length === 2) {
            let html = splitByTag.join(
                `<span class="text-st-active">${this._stringifyCarbonImpacts()}</span>`,
            );

            html = `<div>${html}</div>`;
            this.htmlPvCarbonImpacts = html;
        }
    }

    private _stringifyCarbonImpacts() {
        const pvCarbonImpacts = Math.round(this.solarPvSurplusService.carbonImpacts);
        const pvCarbonImpactsStringified = this.usefulService.stringifyNumber(pvCarbonImpacts);
        return `${pvCarbonImpactsStringified}&nbsp;kgCO<sub>2</sub>/an`;
    }

    private _updatePvElectricMobility() {
        let html = this.solarPvTotalityService.pvInfo.message.mobilite_electrique;

        // pv_em_distance
        const pvEmDistance = Math.round(
            this.solarPvSurplusService.electricMobilityDistanceFromSelfConsumption,
        );
        const pvEmDistanceStringified = this.usefulService.stringifyNumber(pvEmDistance);
        const newPvEmDistance = `<span class="text-st-active">${pvEmDistanceStringified}&nbsp;km/an</span>`;
        html = html.replace('##pv_em_distance', newPvEmDistance);

        // pv_em_distance_ratio
        const pvEmDistanceRatio = Math.round(
            this.solarPvSurplusService.electricMobilityDistanceRatio * 100,
        );
        const pvEmDistanceRatioStringified = this.usefulService.stringifyNumber(pvEmDistanceRatio);
        const newPvEmDistanceRatio = `<span class="text-st-active">${pvEmDistanceRatioStringified}&nbsp;%</span>`;
        html = html.replace('##pv_em_distance_ratio', newPvEmDistanceRatio);

        // pv_em_economy_energy
        const pvEmEconomyEnergy = Math.round(
            this.solarPvSurplusService.electricMobilityAnnualSelfConsumption,
        );
        const pvEmEconomyEnergyStringified = this.usefulService.stringifyNumber(pvEmEconomyEnergy);
        const newPvEmEconomyEnergy = `<span class="text-st-active">${pvEmEconomyEnergyStringified}&nbsp;kWh/an</span>`;
        html = html.replace('##pv_em_economy_energy', newPvEmEconomyEnergy);

        // pv_em_economy
        const pvEmEconomy = Math.round(this.solarPvSurplusService.electricMobilityEconomy);
        const pvEmEconomyStringified = this.usefulService.stringifyNumber(pvEmEconomy);
        const newPvEmEconomy = `<span class="text-st-active">${pvEmEconomyStringified}&nbsp;€/an</span>`;
        html = html.replace('##pv_em_economy', newPvEmEconomy);

        const isConfigOptimal = this.solarPvSurplusService.electricMobilityData.isOptimal;
        if (!isConfigOptimal) {
            // pv_em_optimal_economy_energy
            const pvEmOptimalEconomyEnergy = Math.round(
                this.solarPvSurplusService.optimalElectricMobilityAnnualSelfConsumption,
            );
            const pvEmOptimalEconomyEnergyStringified =
                this.usefulService.stringifyNumber(pvEmOptimalEconomyEnergy);

            // pv_em_optimal_economy
            const pvEmOptimalEconomy = Math.round(
                this.solarPvSurplusService.optimalElectricMobilityEconomy,
            );
            const pvEmOptimalEconomyStringified =
                this.usefulService.stringifyNumber(pvEmOptimalEconomy);

            const optimalConfigText = `
            <div>
                <small>
                    Un profil de charge optimal (Recharge en pleine journée, prise renforcée, 5 recharges
                    par semaine) vous permettrait d’économiser <span>${pvEmOptimalEconomyEnergyStringified}&nbsp;kWh/an</span>
                    soit <span>${pvEmOptimalEconomyStringified}&nbsp;€/an</span>.
                </small>
            </div>`;
            html += optimalConfigText;
        }

        this.htmlPvElectricMobility = html;
    }

    private _setPvElectricMobilityDetail() {
        const pvEmDistance = Math.round(
            this.solarPvSurplusService.electricMobilityDistanceFromSelfConsumption,
        );
        const pvEmDistanceStringified = this.usefulService.stringifyNumber(pvEmDistance);

        const pvEmEconomyEnergy = Math.round(
            this.solarPvSurplusService.electricMobilityAnnualSelfConsumption,
        );
        const pvEmEconomyEnergyStringified = this.usefulService.stringifyNumber(pvEmEconomyEnergy);

        const pvEmEconomy = Math.round(this.solarPvSurplusService.electricMobilityEconomy);
        const pvEmEconomyStringified = this.usefulService.stringifyNumber(pvEmEconomy);

        this.htmlPvElectricMobilityDetail = `
            <p>
                Votre installation vous permet de réaliser <span class="text-st-active">${pvEmDistanceStringified}&nbsp;km/an</span>
                alimentés par vos panneaux solaires.
            </p>
            <p class="fw-bold mb-0">
                Votre profil de charge vous permet d'économiser <span class="text-st-active">${pvEmEconomyEnergyStringified}&nbsp;kWh/an</span>
                soit <span class="text-st-active">${pvEmEconomyStringified}&nbsp;€/an</span>.
            </p>
        `;

        this.electricMobilityData = {
            distanceRatio: this.solarPvSurplusService.electricMobilityDistanceRatio,
        };
    }

    async getConnectionCost() {
        this.connectionCost.isLoading = true;
        this.connectionCost.isActive = true;

        try {
            const peakPower = this.solarPvSurplusService.currentResult.peakPower;
            const result = await this.solarService.getConnectionCost(
                'Surplus',
                this.solarPvSurplusService.consumptionData.subscribedPower,
                peakPower,
            );

            if (result.state) {
                this.connectionCost.value = result.cost.total;
            } else {
                this.connectionCost.message = result.message;
            }

            this.solarPvSurplusService.addConnectionCost(this.connectionCost);
            this._updateAutoconsumptionWithConnectionCost();

            this.connectionCost.isActive = false;
        } catch (error) {
            console.error('Error getConnectionCost', error);
            this.notification.error(
                `L'API Enedis Raccordement ne répond pas. Veuillez réessayer ultérieurement.`,
            );
            this.connectionCost.isActive = true;
        } finally {
            this.connectionCost.isLoading = false;
        }
    }

    async downloadPdf() {
        try {
            this.isResultsDownloading = true;

            const currentResult = this.solarPvSurplusService.currentResult;

            const results = {
                technology: 'pv',
                valorization: 'Surplus',
                totalPowerPeak: currentResult.peakPower,
                htmlInstallation: this.htmlPvInstallation,
                htmlInvestment: this.htmlPvInvestment,
                htmlProduction: this.htmlPvProduction,
                htmlCarbonImpacts: this.htmlPvCarbonImpacts,
                htmlEconomy: this.htmlPvEconomy,
                htmlElectricMobility: this.htmlPvElectricMobility,
                htmlProductionDetail: this.htmlPvProductionDetail,
                htmlConsumptionDetail: this.htmlPvConsumptionDetail,
                htmlElectricMobilityDetail: this.htmlPvElectricMobilityDetail,
                electricMobilityData: this.electricMobilityData,
                gaugeChartData: this.gaugeChartData,
                barChartData: this.barChartData,

                moduleArea: currentResult.totalModuleArea,
                moduleCount: this.slider.moduleCount,
                peakPower: this.solarPvSurplusService.totalPowerPeak,
                sellingGain: this.solarPvSurplusService.sellingGain,
                totalGainByYear: this.solarPvSurplusService.totalGain,
                production: this.solarPvSurplusService.totalProduction,
                surplusEnergy: this.solarPvSurplusService.surplusEnergy,
                selfConsumptionRate: this.solarPvSurplusService.selfConsumptionRate,
                energySelfConsumed: this.solarPvSurplusService.selfConsumptionEnergy,
                investment: this.solarPvSurplusService.investment,
                connectionCost: this.connectionCost.value,
                economyByYear: this.solarPvSurplusService.saving,
                roi: currentResult.roi,
            };

            const buffer = await this.solarService.getSolarPdfFile(results);
            const blob = new Blob([buffer], { type: 'application/pdf' });
            saveAs(blob, 'solaire-photovoltaique.pdf');
        } catch (error) {
            console.error('Error downloadPdf', error);
            this.notification.error(
                'Une erreur est survenue. Impossible de télécharger les résultats.',
            );
        } finally {
            this.isResultsDownloading = false;
        }
    }

    openContactModal() {
        const profileName = environment.name;

        if (profileName === 'cadastre_esterel') {
            window.open(
                'https://france-renov.gouv.fr/annuaires-professionnels/artisan-rge-architecte#/tab',
                '_blank',
            );
        } else {
            const modalRef = this.modalService.open(CadastreSolaireContactModalComponent, {
                modalDialogClass: profileName,
            });
            modalRef.result.then(
                (result) => {},
                () => {},
            );
        }
    }

    validate() {
        const currentResult = this.solarPvSurplusService.currentResult;

        const result = {
            technology: 'pv',
            valorization: 'Surplus',
            label: `Photovoltaïque autoconsommation avec revente du surplus - ${this._stringifyPeakPower()}`,
            totalPowerPeak: currentResult.peakPower,
            htmlInstallation: this.htmlPvInstallation,
            htmlCarbonImpacts: this.htmlPvCarbonImpacts,
            htmlInvestment: this.htmlPvInvestment,
            htmlProduction: this.htmlPvProduction,
            htmlEconomy: this.htmlPvEconomy,
            htmlElectricMobility: this.htmlPvElectricMobility,
            htmlProductionDetail: this.htmlPvProductionDetail,
            htmlConsumptionDetail: this.htmlPvConsumptionDetail,
            htmlElectricMobilityDetail: this.htmlPvElectricMobilityDetail,
            electricMobilityData: this.electricMobilityData,
            gaugeChartData: this.gaugeChartData,
            barChartData: this.barChartData,

            moduleArea: currentResult.totalModuleArea,
            moduleCount: this.slider.moduleCount,
            peakPower: this.solarPvSurplusService.totalPowerPeak,
            sellingGain: this.solarPvSurplusService.sellingGain,
            totalGainByYear: this.solarPvSurplusService.totalGain,
            production: this.solarPvSurplusService.totalProduction,
            surplusEnergy: this.solarPvSurplusService.surplusEnergy,
            selfConsumptionRate: this.solarPvSurplusService.selfConsumptionRate,
            energySelfConsumed: this.solarPvSurplusService.selfConsumptionEnergy,
            investment: this.solarPvSurplusService.investment,
            connectionCost: this.connectionCost.value,
            economyByYear: this.solarPvSurplusService.saving,
            roi: currentResult.roi,

            surplusResult: currentResult,
        };
        this.solarService.addResult(result);

        this.solarService.updateMainStage('resultsStage');
    }

    private _stringifySellingGain() {
        const sellingGain = this.usefulService.round(this.solarPvSurplusService.sellingGain);
        const sellingGainStringified = this.usefulService.stringifyNumber(sellingGain);
        return `${sellingGainStringified}\u00a0€/an`;
    }

    private _stringifyTotalGain() {
        const totalGain = this.usefulService.round(this.solarPvSurplusService.totalGain);
        const totalGainStringified = this.usefulService.stringifyNumber(totalGain);
        return `${totalGainStringified}\u00a0€/an`;
    }

    private _stringifyRoi(data: WithRoi) {
        const pvRoi = this.usefulService.round(data.roi);
        const unit = pvRoi > 1 ? 'ans' : 'an';
        const pvRoiStringified = this.usefulService.stringifyNumber(pvRoi);

        return `${pvRoiStringified} ${unit}`;
    }

    // private _stringifySurplusEnergy() {
    //     const surplusEnergy = Math.round(this.solarPvSurplusService.surplusEnergy);
    //     const surplusEnergyStringified = this.usefulService.stringifyNumber(surplusEnergy);
    //     return `${surplusEnergyStringified}\u00a0kWh/an`;
    // }

    updateModuleCountForAutoconsumption() {
        this.initConnectionCost();

        const pvTotalityResult = this.solarPvTotalityService.findResultByModuleCount(
            this.slider.moduleCount,
        );
        if (!pvTotalityResult) return;
        this.solarPvTotalityService.setCurrentResult(pvTotalityResult);

        const result = this.solarPvSurplusService.findResultByModuleCount(this.slider.moduleCount);
        if (!result) return;

        this.solarPvSurplusService.setCurrentResult(result);
        this.currentResult = result;

        this.slider.moduleArea = this.usefulService.round(result.totalModuleArea);
        this.slider.peakPower = result.peakPower;
        this.slider.stringifiedPeakPower = this.usefulService.stringifyNumber(
            this.usefulService.ceil(result.peakPower, 1),
        );

        this._updatePvIndicator();
    }

    private _updateAutoconsumptionWithConnectionCost() {
        this._updatePvInvestment();
        if (this.connectionCost.value) {
            this._updateAutoconsumptionGain();
            this._updateAutoConsumptionDetail();
        }
    }

    private _updateAutoconsumptionGain(originalRoi?: number) {
        const roi = this.usefulService.round(this.solarPvSurplusService.roi);
        const roiUnit = roi > 1 ? 'ans' : 'an';

        this.warningMessageEconomy = this.solarService.setWarningRoi(roi);

        const pvTotalityResult = this.solarPvTotalityService.currentResult;
        const roiWithTotalSell = this._stringifyRoi(pvTotalityResult);

        const totalSellingMessage = pvTotalityResult.roi
            ? `En vente totale, <span class="text-st-active">le temps de retour sur investissement serait de ${roiWithTotalSell}</span>.`
            : `En vente totale, un montant de revente de 0 €/an ne permet pas au projet d'être rentable économiquement.`;

        const roiWithoutBankLoanMessage = originalRoi
            ? ` (sans emprunt bancaire le temps de retour sur investissement est de <span class="text-st-active">${this._stringifyRoi({ roi: originalRoi })}</span>)`
            : '';

        const htmlPvEconomy = `
            <p>
                Cet investissement vous permet un gain de <span class="text-st-active">${this._stringifyTotalGain()}</span>
                en autoconsommation avec revente du surplus au prix actuel de l'électricité.
                Soit un temps de retour sur investissement de <span class="text-st-active">${roi} ${roiUnit}</span>${roiWithoutBankLoanMessage}.
            </p>
            <div>
                <small>
                    Vous économisez <span class="text-st-active">${this._stringifyEconomyByYear()} sur votre facture</span> et 
                    <span class="text-st-active">${this._stringifySellingGain()} de vente de l'énergie complémentaire</span>.
                </small>
            </div>
            <div>
                <small>
                    ${totalSellingMessage}
                </small>
            </div>
        `;
        this.htmlPvEconomy = htmlPvEconomy;
    }

    private _stringifyEconomyByYear() {
        const economyByYear = this.usefulService.round(this.solarPvSurplusService.saving);
        const economyByYearStringified = this.usefulService.stringifyNumber(economyByYear);
        return `${economyByYearStringified}\u00a0€/an`;
    }

    private _updateAutoConsumptionDetail() {
        this._setHtmlPvConsumptionDetail();
        this._setHtmlPvProductionDetail();
    }

    private _setHtmlPvConsumptionDetail() {
        const housingConsumption = Number(
            this.solarPvSurplusService.consumptionData.energyConsumed,
        );
        const stringifiedHousingConsumption =
            this.usefulService.stringifyNumber(housingConsumption);

        const autoproductionRate = Math.round(this.solarPvSurplusService.selfProductionRate * 100);
        const stringifiedAutoproductionRate =
            this.usefulService.stringifyNumber(autoproductionRate);

        const electricEnergyAutoConsumed = Math.round(
            this.solarPvSurplusService.selfConsumptionEnergy,
        );
        const stringifiedElectricEnergyAutoConsumed = this.usefulService.stringifyNumber(
            electricEnergyAutoConsumed,
        );

        const saving = this.usefulService.round(this.solarPvSurplusService.saving);
        const stringifiedEconomy = this.usefulService.stringifyNumber(saving);

        this.htmlPvConsumptionDetail = `
            <p>
                Vous consommez ${stringifiedHousingConsumption}&nbsp;kWh par an d’électricité.
            </p>
            <p>
                Cette installation couvrirait <span class="text-st-active">${stringifiedAutoproductionRate} %</span>
                de votre consommation d’électricité et vous permettrait donc d’économiser
                <span class="text-st-active">${stringifiedElectricEnergyAutoConsumed}&nbsp;kWh</span> d’achat d’électricité chaque année.
            </p>
            <p class="fw-bold mb-0">
                Cela représente <span class="text-st-active">une économie de ${stringifiedEconomy}&nbsp;€ par an</span> sur votre facture.
            </p>
        `;
        this.gaugeChartData = {
            housingConsumption: housingConsumption,
            electricEnergyAutoConsumed: this.solarPvSurplusService.selfConsumptionEnergy,
            autoproductionRate: this.solarPvSurplusService.selfProductionRate,
        };
    }

    // private _stringifyEnergySelfConsumed() {
    //     const electricEnergySelfConsumed = Math.round(
    //         this.solarPvSurplusService.selfConsumptionEnergy,
    //     );
    //     const stringifiedEnergySelfConsumed = this.usefulService.stringifyNumber(
    //         electricEnergySelfConsumed,
    //     );
    //     return `${stringifiedEnergySelfConsumed}\u00a0kWh`;
    // }

    private _setHtmlPvProductionDetail() {
        const totalProduction = this.usefulService.round(
            this.solarPvSurplusService.totalProduction,
        );
        const stringifiedTotalProduction = this.usefulService.stringifyNumber(totalProduction);

        const sellingGain = this.usefulService.round(this.solarPvSurplusService.sellingGain);
        const stringifiedSellingGain = this.usefulService.stringifyNumber(sellingGain);

        this.htmlPvProductionDetail = `
            <p>
                Avec cette installation, vous produirez au total <span class="text-st-active">
                ${stringifiedTotalProduction}&nbsp;kWh par an</span>.
            </p>
            <p class="fw-bold mb-0">
                En revendant le surplus d’électricité que vous n’autoconsommez pas immédiatement,
                vous êtes rémunérés <span class="text-st-active">${stringifiedSellingGain}&nbsp;€ par an</span>.
            </p>
        `;

        this.barChartData = {
            electricEnergySurplus: this.solarPvSurplusService.surplusEnergy,
            electricEnergyAutoConsumed: this.solarPvSurplusService.selfConsumptionEnergy,
            totalProduction: this.solarPvSurplusService.totalProduction,
        };
    }

    private _displayWarningSubcribedPower(): boolean {
        const subscribedPowerValue = this.solarPvSurplusService.consumptionData.subscribedPower;
        const peakPowerValue = this.solarPvSurplusService.currentResult.peakPower;
        return subscribedPowerValue < peakPowerValue;
    }

    private _displayOverHundredWarning(): boolean {
        const peakPowerValue = this.solarPvTotalityService.currentResult.peakPower;
        return peakPowerValue > 100;
    }

    private _displayOverFiveHundredWarning(): boolean {
        const peakPowerValue = this.solarPvTotalityService.currentResult.peakPower;
        return peakPowerValue > 500;
    }

    get bankLoanTemplateOrNull(): TemplateRef<any> | null {
        if (!this.modularity.simulationBankLoan) return null;
        if (!this.solarPvSurplusService.investment) return null;
        return this.bankLoanCheckboxTemplate;
    }

    public simulateBankLoan() {
        this.solarService.simulateBankLoan();
    }

    get mainButtonLabel(): string {
        if (!this.modularity.simulations) {
            return this.contactButtonLabel;
        }

        return 'Valider ma simulation';
    }

    get mainButtonAction(): () => void {
        if (!this.modularity.simulations) {
            return () => this.openContactModal();
        }

        if (this.isSimulateBankLoanChecked) {
            return () => this.simulateBankLoan();
        }

        return () => this.validate();
    }
}
